import { useEffect, useState } from "react";
import { Loader2, UtensilsCrossed } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["placed", "confirmed", "on_the_way", "delivered", "cancelled"];
const statusColor = {
  placed: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  on_the_way: "bg-indigo-100 text-indigo-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700",
};

export default function OrdersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/room-orders").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const setStatus = async (id, status) => { await api.put(`/room-orders/${id}`, { status }); toast.success(`Marked ${status}`); load(); };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div>
        <h1 className="text-3xl text-foreground mb-1">Room Service Orders</h1>
        <p className="text-muted-foreground text-sm">Live orders billed to guest room folios.</p>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-md">
          <UtensilsCrossed className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No orders yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((o) => (
            <div key={o.id} className="bg-card border border-border rounded-md p-4" data-testid={`order-${o.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-foreground font-medium">{o.guest_name} · {o.order_type === "table" ? `Table ${o.table}` : `Room ${o.room || "—"}`}</p>
                    <Badge className={`${statusColor[o.status]} border-0 capitalize`}>{o.status}</Badge>
                  </div>
                  <ul className="text-sm text-muted-foreground mt-1">
                    {(o.items || []).map((li, i) => <li key={i}>{li.qty}× {li.name} — £{(li.price * li.qty).toFixed(2)}</li>)}
                  </ul>
                  {o.notes && <p className="text-sm text-muted-foreground mt-1 italic">“{o.notes}”</p>}
                  <p className="text-xs text-muted-foreground mt-1">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-serif text-xl text-foreground mb-2">£{o.total}</p>
                  <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                    <SelectTrigger className="w-40" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
